import React from "react";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Gift } from "lucide-react";
import {
  ALL_MYSTERY_STYLES,
  getBoxStyleBody,
  getBoxStyleLid,
  getBoxStyleLabel,
} from "./MysteryBoxStyles";

/**
 * MysteryBoxEditor — admin/merchant config for the QRISE Mystery Box.
 *
 * Lots/probabilities/segments are configured in the standard segments UI
 * (re-used from the wheel), so this editor focuses on what's specific to
 * Mystery Box: visual style of the box + email customization.
 */
export default function MysteryBoxEditor({ game, onGameChange }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "fr";
  const set = (patch) => onGameChange(patch);
  const styleKey = game.mystery_box_style || "gold_gift";

  return (
    <div className="qrise-card p-6 space-y-5" data-testid="mystery-editor">
      <div className="flex items-center gap-2">
        <Gift className="h-4 w-4 text-amber-400" />
        <div className="text-sm font-semibold">
          {t("mystery.editor_title", "Configuration Boîte mystère")}
        </div>
      </div>

      <div>
        <Label className="text-xs font-medium">
          {t("mystery.editor_style", "Style de la boîte")}
        </Label>
        <div className="mt-2 grid grid-cols-5 gap-2">
          {ALL_MYSTERY_STYLES.map((key) => {
            const selected = styleKey === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => set({ mystery_box_style: key })}
                className={`relative aspect-square rounded-xl border-2 transition flex items-center justify-center overflow-hidden ${
                  selected
                    ? "border-amber-500 bg-amber-500/10 ring-2 ring-amber-500/30"
                    : "border-border hover:bg-muted/40"
                }`}
                data-testid={`mystery-style-${key}`}
                aria-label={getBoxStyleLabel(key, lang)}
                title={getBoxStyleLabel(key, lang)}
              >
                <div className="relative w-full h-full">
                  <img
                    src={getBoxStyleBody(key)}
                    alt=""
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  <img
                    src={getBoxStyleLid(key)}
                    alt=""
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                </div>
              </button>
            );
          })}
        </div>
        <p className="mt-1.5 text-[11px] text-muted-foreground leading-snug">
          {t(
            "mystery.editor_style_hint",
            "Le style choisi est affiché aux joueurs sur la page publique et dans l'animation d'ouverture.",
          )}
        </p>
      </div>

      <div className="border-t border-border pt-4 space-y-4">
        <div>
          <Label className="text-xs font-medium">
            {t("mystery.editor_email_subject", "Objet de l'email du code (optionnel)")}
          </Label>
          <Input
            value={game.mystery_email_subject || ""}
            onChange={(e) => set({ mystery_email_subject: e.target.value })}
            placeholder={t(
              "mystery.editor_email_subject_placeholder",
              "🎁 Ton code pour ouvrir la boîte chez Bistro Lumière",
            )}
            className="mt-1.5 rounded-xl"
            data-testid="mystery-editor-subject"
            maxLength={120}
          />
        </div>
        <div>
          <Label className="text-xs font-medium">
            {t("mystery.editor_email_intro", "Texte d'introduction de l'email (optionnel)")}
          </Label>
          <Textarea
            value={game.mystery_email_intro || ""}
            onChange={(e) => set({ mystery_email_intro: e.target.value })}
            placeholder={t(
              "mystery.editor_email_intro_placeholder",
              "Tu as participé au tirage spécial Saint-Valentin chez Bistro Lumière. Voici ton code pour ouvrir ta boîte mystère…",
            )}
            className="mt-1.5 rounded-xl min-h-[80px]"
            data-testid="mystery-editor-intro"
            maxLength={500}
          />
          <p className="mt-1.5 text-[11px] text-muted-foreground leading-snug">
            {t(
              "mystery.editor_email_intro_hint",
              "Si laissé vide, un texte par défaut est utilisé. Maximum 500 caractères.",
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
